import React, { useState } from 'react';
import { Box, Typography, List, ListItemButton, Table, TableHead, TableRow, ListItemText, Paper, TextField, TableCell, TableBody, Alert } from '@mui/material';
import '../style/Components.css';
import ButtonBox from './ButtonBox';
import IconButtonBox from './IconButtonBox';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useThingStore from '../stores/ThingStore';
import { ThingKey, ThingConfigType } from '../types/ThingType';
import DialogBox from './DialogBox';
import TextInputBox from './TextInputBox';
import SelectBox from './SelectBox';
import { getRandomId, isArray, isJSON } from '../utils/functions';
import ThingApi from '../api/ThingApi';

interface ThingConfigProps {
    title?: string;
}

const ThingConfig: React.FC<ThingConfigProps> = ({
    title,
}) => {
    const { labelConfigs, setLabelConfigs, thingObj } = useThingStore();
    const [error, setError] = useState('');
    const [delOpen, setDelOpen] = useState(false);

    const [formValues, setFormValues] = useState({
        editFlag: false,
        selectedConfig: '',
        selectedConfigView: '',
        showEditTitle: false,
        editTitle: '',
        showEditKey: false,
        editKey: '',
        editViewKeyId: '',
        editValue: '',
        editValueType: '',
        fromCreate: true,
        isNewConfig: false,
    });

    const handleOpen = (type: string) => {
        if (type === 'config') {
            changeFormValues('showEditTitle', true);
        } else {
            changeFormValues('showEditKey', true);
        }
    };
    const handleClose = (type: string) => {
        changeFormValues('fromCreate', true);

        if (type === 'config') {
            changeFormValues('showEditTitle', false);
        } else {
            changeFormValues('showEditKey', false);
        }
    };

    const changeFormValues = (field: string, value: any) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    };

    const handleAddConfig = () => {
        setError('');
        if (formValues.editTitle) {
            if (!labelConfigs.some(config => config.name === formValues.editTitle)) {
                const newConfig: ThingConfigType = {
                    name: formValues.editTitle,
                    viewId: getRandomId(),
                    dataKeys: []
                };

                labelConfigs.push(newConfig);

                changeFormValues('editTitle', '');
                handleClose('config');
            } else {
                setError('Identity already exists.');
            }
        } else {
            setError('Identity is empty.');
        }
    };

    const handleAddKey = () => {
        setError('');
        if (formValues.editKey) {
            if (formValues.editValueType) {
                if (formValues.editValueType === 'JSON' && !isJSON(formValues.editValue)) {
                    setError('JSON Type Error.');
                    return;
                } else if (formValues.editValueType === 'Array' && !isArray(formValues.editValue)) {
                    setError('Array Type Error.');
                    return;
                }

                if (formValues.fromCreate) {
                    let isExist = labelConfigs.find(config => config.viewId === formValues.selectedConfigView)?.dataKeys
                        .find((key) => key.key === formValues.editKey);

                    if (!isExist) {
                        const newKey: ThingKey = {
                            viewKeyId: getRandomId(),
                            key: formValues.editKey,
                            value: formValues.editValue,
                            valueType: formValues.editValueType,
                        };

                        labelConfigs.find(config => config.viewId === formValues.selectedConfigView)?.dataKeys.push(newKey);

                        changeFormValues('editKey', '');
                        changeFormValues('editValue', '');
                        changeFormValues('editValueType', '');

                        handleClose('key');
                    } else {
                        setError('Key already exists.');
                    }
                } else {
                    let obj = labelConfigs.find(config => config.viewId === formValues.selectedConfigView)?.dataKeys
                        .find((key) => key.key === formValues.editKey);

                    if (obj && obj.viewKeyId !== formValues.editViewKeyId) {
                        setError('Key already exists.');
                    } else {
                        const newKey = labelConfigs.find(
                            config => config.viewId === formValues.selectedConfigView)?.dataKeys
                            .map((key) => {
                                return key.viewKeyId === formValues.editViewKeyId
                                    ? {
                                        key: formValues.editKey,
                                        value: formValues.editValue,
                                        valueType: formValues.editValueType,
                                    } : key
                            }) as ThingKey[];

                        const updatedConfigs = labelConfigs.map((config) =>
                            config.viewId === formValues.selectedConfigView && formValues.selectedConfig
                                ? { ...config, dataKeys: newKey ? newKey : [] }
                                : config
                        );

                        setLabelConfigs(updatedConfigs);
                        changeFormValues('editKey', '');
                        changeFormValues('editValue', '');
                        changeFormValues('editValueType', '');
                        handleClose('key');
                    }
                }
            } else {
                setError('Value type is empty.');
            }
        } else {
            setError('Key is empty.');
        }

    }

    const modifyTitle = () => {

        if (formValues.editFlag) {
            if (!labelConfigs.some(config => config.name === formValues.selectedConfig)) {
                const updatedConfigs = labelConfigs.map((config) =>
                    config.viewId === formValues.selectedConfigView && formValues.selectedConfig
                        ? { ...config, name: formValues.selectedConfig }
                        : config
                );

                setLabelConfigs(updatedConfigs);
            }
        }

        changeFormValues('editFlag', !formValues.editFlag);

    };

    const openDelete = () => {
        if (formValues.selectedConfigView !== '') {
            setDelOpen(true);
        }
    }

    const deleteConfig = async () => {
        if (formValues.selectedConfigView !== '') {
            if (labelConfigs.some(config => config.viewId === formValues.selectedConfigView)) {

                if (labelConfigs.some(config => config.viewId === config.name)) {
                    await ThingApi.deleteConfig(thingObj.id || '', formValues.selectedConfigView);
                }

                const updatedConfigs = labelConfigs.filter(config => config.viewId !== formValues.selectedConfigView);
                setLabelConfigs(updatedConfigs);
            }
            changeFormValues('selectedConfig', '');
            changeFormValues('selectedConfigView', '');
        }

        setDelOpen(false);
    }

    const editKey = (index: number) => {
        let key = labelConfigs.find(config => config.viewId === formValues.selectedConfigView)?.dataKeys;
        if (key) {
            changeFormValues('editKey', key[index].key);
            changeFormValues('editViewKeyId', key[index].viewKeyId);
            changeFormValues('editValue', key[index].value);
            changeFormValues('editValueType', key[index].valueType);

            changeFormValues('fromCreate', false);

            handleOpen('key');
        }

    }

    const deleteKey = (index: number) => {
        const updatedKeys = labelConfigs.map((config) =>
            config.viewId === formValues.selectedConfigView && formValues.selectedConfig
                ? { ...config, dataKeys: config.dataKeys.filter((_, i) => i !== index) }
                : config
        );

        setLabelConfigs(updatedKeys);
    };


    const updateKeyData = (config: any) => {
        if (config.name === config.viewId) {
            changeFormValues('isNewConfig', false);
        } else {
            changeFormValues('isNewConfig', true);
        }

        changeFormValues('selectedConfig', config.name);
        changeFormValues('selectedConfigView', config.viewId);
    }

    return (
        <Box sx={{ mb: 4, display: 'flex', gap: 4 }}>
            <Box className='w220'>
                <Typography variant="h5" gutterBottom>{title}</Typography>
                <ButtonBox
                    text='Add a config group'
                    onClick={() => handleOpen('config')}
                    icon={<AddIcon />} />
                <List sx={{ width: 220, backgroundColor: '#f9f9f9', height: 200, overflowY: 'auto' }}>
                    {labelConfigs.map((config, index) => (
                        <ListItemButton
                            selected={config.name === formValues.selectedConfig}
                            onClick={() => updateKeyData(config)}
                            key={config.name}
                        >
                            <ListItemText primary={config.name} />
                        </ListItemButton>
                    ))}
                </List>

            </Box>

            {formValues.selectedConfig &&
                <Box className='flex1'>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {formValues.editFlag ?
                                    (
                                        <TextField
                                            value={formValues.selectedConfig}
                                            variant="standard"
                                            size="small"
                                            style={{ width: "100px" }}
                                            onChange={(e) => changeFormValues('selectedConfig', e.target.value)}
                                        />
                                    ) :
                                    (
                                        <Typography variant="h6">{formValues.selectedConfig}</Typography>
                                    )
                                }
                                {formValues.isNewConfig && (
                                    <IconButtonBox
                                        icon={<EditIcon />}
                                        onClick={modifyTitle} />
                                )}
                            </Box>
                            <Box>
                                <IconButtonBox
                                    icon={<DeleteOutlineOutlinedIcon />}
                                    text='Delete the group'
                                    onClick={openDelete} />
                                <ButtonBox
                                    text='Add a key'
                                    onClick={() => handleOpen('key')}
                                    icon={<AddIcon />} />
                            </Box>
                        </Box>

                        <Table sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Key</TableCell>
                                    <TableCell>Reported value</TableCell>
                                    <TableCell>Expected value</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {labelConfigs
                                    .find(config => config.viewId === formValues.selectedConfigView)?.dataKeys
                                    .map((key, index) => (
                                        <TableRow key={key.key}>
                                            <TableCell>{key.key}</TableCell>
                                            <TableCell>NULL</TableCell>
                                            <TableCell>{key.value}</TableCell>
                                            <TableCell>
                                                <IconButtonBox icon={<EditIcon />} onClick={() => editKey(index)} />
                                                <IconButtonBox icon={<DeleteOutlineOutlinedIcon />} onClick={() => deleteKey(index)} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            }
            <DialogBox
                open={formValues.showEditTitle}
                title="Add a config group"
                dialogContent={
                    <>
                        {error && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutlineIcon />}
                                className='login_error_alert'
                            >
                                {error}
                            </Alert>
                        )}
                        <TextInputBox
                            label="Identity"
                            value={formValues.editTitle}
                            onChange={(e) => changeFormValues('editTitle', e.target.value)}
                        />
                    </>
                }
                onCancel={() => handleClose('config')}
                onConfirm={handleAddConfig}
                confirmLabel="Add"
            />
            <DialogBox
                open={formValues.showEditKey}
                title="Add a key"
                dialogContent={
                    <>
                        {error && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutlineIcon />}
                                className='login_error_alert'
                            >
                                {error}
                            </Alert>
                        )}
                        <TextInputBox
                            label="Key"
                            required={true}
                            value={formValues.editKey}
                            onChange={(e) => changeFormValues('editKey', e.target.value)}
                        />
                        <SelectBox
                            label="Value type"
                            name="valueType"
                            required={true}
                            value={formValues.editValueType}
                            options={[
                                { value: 'string', label: 'String' },
                                { value: 'number', label: 'Number' },
                                { value: 'boolean', label: 'Boolean' },
                                { value: 'JSON', label: 'JSON' },
                                { value: 'Array', label: 'Array' },
                            ]}
                            onChange={(e) => changeFormValues('editValueType', e.target.value)}
                        />
                        <TextInputBox
                            label="Value"
                            value={formValues.editValue}
                            onChange={(e) => changeFormValues('editValue', e.target.value)}
                        />
                    </>
                }
                onCancel={() => handleClose('key')}
                onConfirm={handleAddKey}
                confirmLabel={formValues.fromCreate ? "Add" : "Update"}
            />
            <DialogBox
                open={delOpen}
                title='Delete'
                dialogContent={
                    <>
                        {formValues.selectedConfig} will be permanently deleted.
                    </>
                }
                showCancel={true}
                onCancel={() => setDelOpen(false)}
                onConfirm={deleteConfig}
                isError={true}
                confirmLabel='Delete'
            />
        </Box>
    );
};

export default ThingConfig;
